import type { RobotProfile } from '../profiles/types';
import type { SafetyOutput } from '../sim/safety';
import type { SurfaceProps } from '../sim/surfaces';

/**
 * Дифференциальный привод: скорость хода и скорость поворота независимы,
 * возможен разворот на месте. Робот не исполняет команду мгновенно —
 * задержка привода, ограничения разгона/торможения и сцепление с покрытием
 * дают ту самую инерцию, которую оператор должен научиться упреждать.
 */
const DEG = Math.PI / 180;

export interface DriveOutput {
  /** Продольная скорость, м/с (со знаком) */
  speed: number;
  /** Угловая скорость, град/с */
  yawRate: number;
}

interface Pending {
  t: number;
  v: number;
  w: number;
}

export class DifferentialDrive {
  private speed = 0;
  private yawRate = 0;
  private pending: Pending[] = [];
  private appliedV = 0;
  private appliedW = 0;

  constructor(private p: RobotProfile) {}

  reset(): void {
    this.speed = 0;
    this.yawRate = 0;
    this.pending.length = 0;
    this.appliedV = 0;
    this.appliedW = 0;
  }

  /** Текущая продольная скорость (для телеметрии и машины состояний) */
  get currentSpeed(): number {
    return this.speed;
  }

  get currentYawRate(): number {
    return this.yawRate;
  }

  /** Механическая скорость извне (робот скатился с горки, столкнулся) */
  syncSpeed(v: number, w: number): void {
    this.speed = v;
    this.yawRate = w;
  }

  step(now: number, dt: number, out: SafetyOutput, surf: SurfaceProps, slopeAccel: number): DriveOutput {
    // Задержка привода: команда доехала, но колёса тронутся не сразу
    this.pending.push({ t: now + this.p.drive_latency_ms, v: out.v, w: out.w });
    while (this.pending.length > 0 && this.pending[0].t <= now) {
      const c = this.pending.shift()!;
      this.appliedV = c.v;
      this.appliedW = c.w;
    }

    let targetSpeed: number;
    let targetYaw: number;
    let accelCap: number;
    let decelCap: number;

    switch (out.mode) {
      case 'emergency':
        targetSpeed = 0;
        targetYaw = 0;
        accelCap = 0;
        decelCap = this.p.emergency_decel_ms2 * surf.grip;
        break;
      case 'full_stop':
        targetSpeed = 0;
        targetYaw = 0;
        accelCap = 0;
        decelCap = this.p.max_decel_ms2 * surf.grip;
        break;
      case 'smooth_stop':
        targetSpeed = 0;
        targetYaw = 0;
        accelCap = 0;
        decelCap = this.p.max_decel_ms2 * 0.6 * surf.grip;
        break;
      case 'command': {
        const v = this.appliedV;
        targetSpeed = (v >= 0 ? v * this.p.max_speed_ms : v * this.p.max_reverse_ms) * surf.speedFactor;
        const speedFrac = Math.min(1, Math.abs(this.speed) / this.p.max_speed_ms);
        const turnScale = 1 - (1 - this.p.turn_rate_at_max_speed) * speedFrac;
        targetYaw = this.appliedW * this.p.turn_rate_max_dps * turnScale;
        accelCap = this.p.max_accel_ms2 * surf.grip;
        decelCap = this.p.max_decel_ms2 * surf.grip;
        break;
      }
    }

    // Сопротивление качению — пропорционально скорости: на траве и в грязи
    // робот трогается, но разогнаться и держать ход уже не может
    const rolling = surf.rolling * Math.min(1, Math.abs(this.speed) / this.p.max_speed_ms);
    const drag = Math.sign(this.speed) * rolling * dt;
    if (Math.abs(drag) < Math.abs(this.speed)) this.speed -= drag;
    else this.speed = 0;

    // Сколько привод хотел бы изменить скорость: это комфортные пределы разгона
    // и торможения из профиля, они же ощущаются оператором на ровном месте
    const dv = targetSpeed - this.speed;
    const reducing = Math.abs(targetSpeed) < Math.abs(this.speed) || Math.sign(targetSpeed) !== Math.sign(this.speed);
    const cap = (reducing ? decelCap : accelCap) * dt;
    const request = (Math.sign(dv) * Math.min(Math.abs(dv), cap)) / dt;

    // Что привод реально может: тяга ограничена сцеплением, а её потолок задан
    // паспортным уклоном — робот, рассчитанный на 12°, обязан на 12° держаться.
    // Круче — тяги не хватает, робот сползает; на льду не хватает и на пологом.
    const tract =
      Math.max(this.p.max_accel_ms2, 9.81 * Math.sin(this.p.max_safe_slope_deg * DEG) * 1.25) * surf.grip;
    const driveAccel = Math.max(-tract, Math.min(tract, request - slopeAccel));
    this.speed += (driveAccel + slopeAccel) * dt;

    const dw = targetYaw - this.yawRate;
    const wCap = this.p.turn_accel_dps2 * surf.grip * dt;
    this.yawRate += Math.sign(dw) * Math.min(Math.abs(dw), wCap);

    return { speed: this.speed, yawRate: this.yawRate };
  }
}
